import './assets/scss/style.scss'

import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom'
import moment from 'moment'
import React, { Suspense, useCallback, useEffect } from 'react'
import { createBrowserHistory } from 'history'
import { useAppDispatch, useTypedSelector } from './stateStore'
import ProtectRoute from './components/ProtectRoutes'
import SessionTimeout from './components/SessionTimeout'
import { reduxServices } from './reducers/reduxServices'
import OLoadingSpinner from './components/ReusableComponent/OLoadingSpinner'
import { LoadingType } from './types/Components/loadingScreenTypes'
import ErrorPage from './pages/ErrorPage/ErrorPage'
import { lazyRetry } from './utils/reactLazyRetryUtils'
import ErrorPolicy from './pages/ErrorPage/ErrorPolicy'
import VendorLogin from './pages/Login/VendorLogin'
import { getDefaultLoginRedirect } from './utils/domainUtils'

// Containers
const DefaultLayout = React.lazy(() =>
  lazyRetry(() => import('./layout/DefaultLayout'), 'DefaultLayout'),
)
const Login = React.lazy(() =>
  lazyRetry(() => import('./pages/Login/Login'), 'Login'),
)

const App = (): JSX.Element => {
  moment.suppressDeprecationWarnings = true
  const history = createBrowserHistory()
  const styleTenantKey = localStorage.getItem('tenantKey')
  const setIsSessionExpired =
    useTypedSelector(reduxServices.app.selectors.selectIsSessionExpired) ??
    false
  const employeeId = useTypedSelector(
    reduxServices.authentication.selectors.selectEmployeeId,
  )

  const authenticatedToken = useTypedSelector(
    reduxServices.authentication.selectors.selectToken,
  )
  const dispatch = useAppDispatch()

  const loadState = useCallback(() => {
    const employeeNameFromStorage = localStorage.getItem('employeeName')
    const employeeIdFromStorage = localStorage.getItem('employeeId')
    const userNameFromStorage = localStorage.getItem('userName')
    const roleFromStorage = localStorage.getItem('role')
    const tenantKeyFromStorage = localStorage.getItem('tenantKey')
    const tokenFromStorage = localStorage.getItem('token')
    const vendorEmployeeIdFromStorage = localStorage.getItem('vendorEmployeeId')

    return {
      authenticatedUser: {
        employeeName: employeeNameFromStorage,
        employeeId: employeeIdFromStorage,
        userName: userNameFromStorage,
        role: roleFromStorage,
        tenantKey: tenantKeyFromStorage,
        token: tokenFromStorage,
        vendorEmployeeId: vendorEmployeeIdFromStorage,
      },
    }
  }, [])

  history.listen(() => {
    // Scroll to the top of the page on each navigation
    window.scrollTo(0, 0)
  })

  useEffect(() => {
    const initialAuthenticationState = loadState()

    dispatch(
      reduxServices.authentication.actions.setAuthentication(
        initialAuthenticationState,
      ),
    )
  })
  const vendorEmployeeIdFromStorage = localStorage.getItem('vendorEmployeeId')
  const isVendor = localStorage.getItem('isVendor') === 'true'

  useEffect(() => {
    if (authenticatedToken != null && employeeId != null) {
      // dispatch(
      //   getEmployeeGeneralInformationThunk.getEmployeeGeneralInformation(
      //     employeeId,
      //   ),
      // )
      //THOUGH DO NOT DELETE THIS CODE FOR FUTURE REFERENCE
      // THIS IS ALREADY REDUNDANT CALL TO THE API SINCE THERE'S ALREADY A CALL ON THE USERPROFILE.TSX
      if (isVendor) {
        dispatch(
          reduxServices.userRolesConfigurations.getFeaturesUnderVendorThunk(
            vendorEmployeeIdFromStorage as string,
          ),
        )
        dispatch(
          reduxServices.userAccessToFeatures.getUserAccessToFeaturesForVendor(
            vendorEmployeeIdFromStorage as string,
          ),
        )
      } else {
        dispatch(
          reduxServices.userRolesConfigurations.getFeaturesUnderEmployeeThunk(
            employeeId as string,
          ),
        )
        dispatch(
          reduxServices.userAccessToFeatures.getUserAccessToFeatures(
            employeeId,
          ),
        )
      }
    }

    // Handle theme changes based on styleTenantKey
    if (authenticatedToken && styleTenantKey === 'AIBRIDGEML') {
      document.body.classList.remove('rbt-theme')
      document.body.classList.add('aiml-theme')
    } else {
      document.body.classList.remove('aiml-theme')
      document.body.classList.add('rbt-theme')
    }
  }, [
    authenticatedToken,
    employeeId,
    isVendor,
    vendorEmployeeIdFromStorage,
    styleTenantKey,
    dispatch,
  ])

  const ScrollToTop = () => {
    const { pathname } = useLocation()
    useEffect(() => {
      if (pathname !== '/newEvent') {
        dispatch(reduxServices.newEvent.actions.clearProjectMembers())
      }
      window.scrollTo(0, 0)
    }, [pathname])
    return null
  }

  return (
    <BrowserRouter basename={process.env.REACT_APP_ROUTER_BASE || ''}>
      <ScrollToTop />
      <Suspense fallback={<OLoadingSpinner type={LoadingType.PAGE} />}>
        <Switch>
          <Route
            path="/sessionExpire"
            render={() =>
              setIsSessionExpired ? <SessionTimeout /> : <Redirect to={'/'} />
            }
          />
          <Route path="/login" render={() => <Login />} />
          <Route path="/vendorLogin" render={() => <VendorLogin />} />
          <Route exact path="/forbidden" component={ErrorPage} />
          <Route exact path="/forbiddenPolicy" component={ErrorPolicy} />
          {/* <Route path="/reset-password" render={() => <VendorResetPswd />} /> */}
          <Route
            exact
            path="/"
            render={() => <Redirect to={getDefaultLoginRedirect()} />}
          />
          <Route
            path="/"
            render={() => (
              <ProtectRoute
                callback={(token) => !!token}
                redirectTo={getDefaultLoginRedirect()}
              >
                <DefaultLayout />
              </ProtectRoute>
            )}
          />
        </Switch>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
